import React, { useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';

const focusableSelector = [
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

export default function BroadcastModal({ broadcast, onAcknowledge }) {
  const [phrase, setPhrase] = useState('');
  const dialogRef = useRef(null);
  const normalizedPhrase = (broadcast?.ack_phrase || 'AGREE').trim().toLowerCase();
  const isMatch = phrase.trim().toLowerCase() === normalizedPhrase;

  useEffect(() => {
    setPhrase('');
  }, [broadcast?.id, broadcast?.version]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelectorAll(focusableSelector);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first) first.focus();
    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      if (focusable.length === 0) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    dialog.addEventListener('keydown', handleKeyDown);
    return () => dialog.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!broadcast) return null;

  const badgeVariant =
    broadcast.severity === 'critical' ? 'critical' : broadcast.severity === 'warning' ? 'warning' : 'info';

  return (
    <div className="sp-dialog-backdrop" role="presentation">
      <div
        className="sp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="broadcast-title"
        aria-describedby="broadcast-body"
        ref={dialogRef}
      >
        <Card className="p-6 space-y-4">
          <div>
            <Badge variant={badgeVariant}>{broadcast.severity}</Badge>
            <h2 id="broadcast-title" className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>
              {broadcast.title}
            </h2>
          </div>
          <div id="broadcast-body" className="text-sm whitespace-pre-wrap">
            {broadcast.body}
          </div>
          <div className="sp-panel sp-panel--muted p-3">
            <div className="sp-label">Acknowledgment required</div>
            <p className="text-xs sp-muted mt-1">
              Type <strong>{broadcast.ack_phrase}</strong> to continue.
            </p>
            <input
              className="sp-input mt-2 w-full"
              value={phrase}
              onChange={(event) => setPhrase(event.target.value)}
              aria-label="Acknowledgment phrase"
            />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs sp-muted">
              This announcement requires acknowledgment to proceed.
            </div>
            <Button size="sm" type="button" onClick={() => onAcknowledge?.(broadcast)} disabled={!isMatch}>
              Acknowledge & continue
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
